/**
 * Catálogo completo de endpoints de la API
 *
 * REGLAS:
 * - Nombre descriptivo en SCREAMING_SNAKE_CASE
 * - Agrupados por dominio
 * - Parámetros en formato :paramName (mismo que backend)
 * - Comentar si es legacy o deprecado
 *
 * IMPORTANTE: Mantener sincronizado con el backend
 * Ver: backend/features/ para referencia
 */

export const API_ENDPOINTS = {
  // ============================================
  // AUTH & AUTHENTICATION
  // ============================================
  AUTH_REGISTER: '/api/auth/register',
  AUTH_LOGIN: '/api/auth/login',
  AUTH_MICROSOFT: '/api/auth/microsoft',
  AUTH_GOOGLE: '/api/auth/google',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_REFRESH: '/api/auth/refresh',
  AUTH_ME: '/api/auth/me',
  AUTH_MY_PERMISSIONS: '/api/auth/my-permissions',
  AUTH_PASSWORD_RESET: '/api/auth/password-reset',
  AUTH_PASSWORD_RESET_CONFIRM: '/api/auth/password-reset/confirm',

  // ============================================
  // USERS
  // ============================================
  USERS_LIST: '/api/users/',  // Barra final para evitar redirect HTTP en Railway
  USERS_DETAIL: '/api/users/:user_id',
  USERS_VALIDATE_IDENTITY: '/api/users/:user_id/validate-identity',
  USERS_STATUS: '/api/users/:user_id/status',
  USERS_PROFILES: '/api/users/:user_id/profiles',
  USERS_PROFILES_DELETE: '/api/users/:user_id/profiles/:profile_id',
  USERS_ACTIVITY: '/api/users/:user_id/activity',
  USERS_DUPLICATES: '/api/users/:user_id/duplicates',
  USERS_DUPLICATES_DISMISS: '/api/users/:user_id/duplicates/dismiss',
  USERS_DUPLICATES_MERGE: '/api/users/:user_id/duplicates/merge',
  USERS_PERMISSIONS: '/api/users/:user_id/permissions',
  USERS_PERMISSIONS_DELETE: '/api/users/:user_id/permissions/:permission_id',

  // ============================================
  // PERMISSIONS
  // ============================================
  PERMISSIONS_LIST: '/api/permissions/',  // Barra final para evitar redirect HTTP en Railway

  // ============================================
  // ACADEMIC - PLANS (Planes de Carrera)
  // ============================================
  PLANS_LIST: '/api/academic/plans/',  // Barra final para evitar redirect HTTP en Railway
  PLANS_CREATE: '/api/academic/plans/',
  PLANS_DETAIL: '/api/academic/plans/:plan_id',
  PLANS_DETAIL_FULL: '/api/academic/plans/:plan_id/detail',
  PLANS_UPDATE: '/api/academic/plans/:plan_id',
  PLANS_DELETE: '/api/academic/plans/:plan_id',
  PLANS_STRUCTURE: '/api/academic/plans/structure',
  PLANS_VALIDATE: '/api/academic/plans/validate',
  PLANS_BUILDER: '/api/academic/plans/builder',
  PLANS_TREE: '/api/academic/plans/:plan_id/tree',
  PLANS_PROGRAM_UPDATE: '/api/academic/plans/:plan_id/programs/:program_id',
  PLANS_HISTORY: '/api/academic/plans/:plan_id/history',
  PLANS_COMPARE: '/api/academic/plans/:plan_id/compare',
  PLANS_RESTORE: '/api/academic/plans/:plan_id/restore/:version',
  PLANS_DELETE_MULTIPLE: '/api/academic/plans/multiple',
  PLANS_VALIDATE_EXISTING: '/api/academic/plans/:plan_id/validate',

  // ============================================
  // ACADEMIC - TEMPLATES (Plantillas de Planes)
  // ============================================
  TEMPLATES_LIST: '/api/academic/templates/',  // Barra final para evitar redirect HTTP en Railway
  TEMPLATES_CREATE: '/api/academic/templates/',  // Barra final para evitar redirect HTTP en Railway
  TEMPLATES_DETAIL: '/api/academic/templates/:template_id',
  TEMPLATES_UPDATE: '/api/academic/templates/:template_id',          // PUT - reemplaza completo
  TEMPLATES_PARTIAL_UPDATE: '/api/academic/templates/:template_id',  // PATCH - solo campos enviados
  TEMPLATES_DELETE: '/api/academic/templates/:template_id',
  TEMPLATES_VISIBILITY: '/api/academic/templates/:template_id/visibility',
  TEMPLATES_EXPORT: '/api/academic/templates/:template_id/export',
  TEMPLATES_IMPORT: '/api/academic/templates/import',

  // ============================================
  // ACADEMIC - MODALITIES (Modalidades Académicas)
  // ============================================
  MODALITIES_LIST: '/api/academic/modalities/',

  // ============================================
  // ACADEMIC - STUDY PLANS (Planes de Estudio)
  // ============================================
  STUDY_PLANS_LIST: '/api/academic/study-plans/',
  STUDY_PLANS_CREATE: '/api/academic/study-plans/',
  STUDY_PLANS_DETAIL: '/api/academic/study-plans/:study_plan_id',
  STUDY_PLANS_UPDATE: '/api/academic/study-plans/:study_plan_id',
  STUDY_PLANS_DELETE: '/api/academic/study-plans/:study_plan_id',
  STUDY_PLANS_CHANGELOG: '/api/academic/study-plans/:study_plan_id/changelog',
  STUDY_PLANS_STATS: '/api/academic/study-plans/stats',

  // RVOE (bajo study-plans)
  STUDY_PLANS_RVOE_LIST: '/api/academic/study-plans/:study_plan_id/rvoe/',
  STUDY_PLANS_RVOE_CURRENT: '/api/academic/study-plans/:study_plan_id/rvoe/current',
  STUDY_PLANS_RVOE_CREATE: '/api/academic/study-plans/:study_plan_id/rvoe/',
  STUDY_PLANS_RVOE_UPDATE: '/api/academic/study-plans/:study_plan_id/rvoe/:rvoe_id',
  STUDY_PLANS_RVOE_STATUS: '/api/academic/study-plans/:study_plan_id/rvoe/:rvoe_id/status',
  STUDY_PLANS_RVOE_START_TRAMITE: '/api/academic/study-plans/:study_plan_id/rvoe/start-tramite/',
  STUDY_PLANS_RVOE_STEPS_LIST: '/api/academic/study-plans/:study_plan_id/rvoe/:rvoe_id/steps/',
  STUDY_PLANS_RVOE_STEP_UPDATE: '/api/academic/study-plans/:study_plan_id/rvoe/:rvoe_id/steps/:step_number',
  STUDY_PLANS_RVOE_STEP_DOC_DELETE: '/api/academic/study-plans/:study_plan_id/rvoe/:rvoe_id/steps/:step_number/documents/:doc_index',

  // Documents (bajo study-plans)
  STUDY_PLANS_DOCS_LIST: '/api/academic/study-plans/:study_plan_id/documents/',
  STUDY_PLANS_DOCS_UPLOAD: '/api/academic/study-plans/:study_plan_id/documents/',
  STUDY_PLANS_DOCS_DOWNLOAD: '/api/academic/study-plans/:study_plan_id/documents/:doc_id/download',
  STUDY_PLANS_DOCS_DELETE: '/api/academic/study-plans/:study_plan_id/documents/:doc_id',

  // ============================================
  // ACADEMIC - SCHOOL UNITS (Escuelas)
  // ============================================
  SCHOOL_UNITS_LIST: '/api/academic/school-units/',
  SCHOOL_UNITS_CREATE: '/api/academic/school-units/',
  SCHOOL_UNITS_DETAIL: '/api/academic/school-units/:school_unit_id',
  SCHOOL_UNITS_UPDATE: '/api/academic/school-units/:school_unit_id',
  SCHOOL_UNITS_DELETE: '/api/academic/school-units/:school_unit_id',
  SCHOOL_UNITS_COORDINATORS: '/api/academic/school-units/coordinators/',

  // ============================================
  // SYSTEM - FIELD PROTECTION
  // ============================================
  FIELD_PROTECTION_EVALUATE: '/api/system/field-protection/:entity/:entity_id/',
  FIELD_PROTECTION_SCHEMA: '/api/system/field-protection/:entity/schema/',

  // ============================================
  // ACADEMIC - PROGRAMS (Programas Académicos)
  // ============================================
  PROGRAMS_LIST: '/api/academic/programs/',  // Barra final para evitar redirect HTTP en Railway
  PROGRAMS_TYPES: '/api/academic/programs/types',
  PROGRAMS_TOTAL_HOURS: '/api/academic/programs/:program_id/total-hours',
  PROGRAMS_TOTAL_HOURS_BATCH: '/api/academic/programs/total-hours-batch/',
  PROGRAMS_RECALCULATE_ALL_HOURS: '/api/academic/programs/recalculate-all-hours',
  PROGRAMS_RECALCULATE_SUBJECT_HOURS: '/api/academic/programs/:program_id/recalculate-subject-hours',
  PROGRAMS_RECALCULATE_ALL_SUBJECT_HOURS: '/api/academic/programs/recalculate-all-subject-hours',

  // ============================================
  // ACADEMIC - SUBJECTS (Materias)
  // ============================================
  SUBJECTS_LIST: '/api/academic/subjects/',  // Barra final para evitar redirect HTTP en Railway
  SUBJECTS_CREATE: '/api/academic/subjects/',
  SUBJECTS_DETAIL: '/api/academic/subjects/:subject_id',
  SUBJECTS_UPDATE: '/api/academic/subjects/:subject_id',
  SUBJECTS_DELETE: '/api/academic/subjects/:subject_id',
  SUBJECTS_TYPES: '/api/academic/subjects/types',
  SUBJECTS_HOURS: '/api/academic/subjects/:subject_id/hours',
  SUBJECTS_HOUR_RULES: '/api/academic/subjects/hour-rules',

  // ============================================
  // ACADEMIC - WORKSHOPS (Talleres Corporativos)
  // ============================================
  WORKSHOPS_LIST: '/api/academic/workshops/',
  WORKSHOPS_CREATE: '/api/academic/workshops/',
  WORKSHOPS_DETAIL: '/api/academic/workshops/:workshop_id',
  WORKSHOPS_UPDATE: '/api/academic/workshops/:workshop_id',
  WORKSHOPS_DELETE: '/api/academic/workshops/:workshop_id',

  // ============================================
  // ACADEMIC - PROJECTS (Proyectos)
  // ============================================
  PROJECTS_LIST: '/api/academic/projects/',  // Barra final para evitar redirect HTTP en Railway
  PROJECTS_DETAIL: '/api/academic/projects/:project_id',

  // ============================================
  // ACADEMIC - RESOURCES (Centros, Campus, Aulas)
  // ============================================
  CENTERS_LIST: '/api/academic/centers/',  // Barra final para evitar redirect HTTP en Railway
  CENTERS_CREATE: '/api/academic/centers/',  // Barra final para evitar redirect HTTP en Railway
  CENTERS_DETAIL: '/api/academic/centers/:center_id',
  CENTERS_UPDATE: '/api/academic/centers/:center_id',
  CENTERS_DELETE: '/api/academic/centers/:center_id',
  CAMPUS_LIST: '/api/academic/campus/',  // Barra final para evitar redirect HTTP en Railway
  CAMPUS_CREATE: '/api/academic/campus/',  // Barra final para evitar redirect HTTP en Railway
  CAMPUS_DETAIL: '/api/academic/campus/:campus_id',
  CAMPUS_UPDATE: '/api/academic/campus/:campus_id',
  CAMPUS_DELETE: '/api/academic/campus/:campus_id',
  CLASSROOMS_LIST: '/api/academic/classrooms/',  // Barra final para evitar redirect HTTP en Railway
  CLASSROOMS_CREATE: '/api/academic/classrooms/',  // Barra final para evitar redirect HTTP en Railway
  CLASSROOMS_DETAIL: '/api/academic/classrooms/:classroom_id',
  CLASSROOMS_UPDATE: '/api/academic/classrooms/:classroom_id',
  CLASSROOMS_DELETE: '/api/academic/classrooms/:classroom_id',

  // ============================================
  // ACADEMIC - INSTRUCTIONAL DESIGN (Diseño Instruccional)
  // ============================================
  ID_SUBJECTS_LIST: '/api/academic/instructional-design/subjects/',
  ID_SUBJECT_SUMMARY: '/api/academic/instructional-design/subjects/:subject_id/summary',
  ID_SUBJECT_OBJECTIVES_LIST: '/api/academic/instructional-design/subjects/:subject_id/objectives/',
  ID_SUBJECT_OBJECTIVES_CREATE: '/api/academic/instructional-design/subjects/:subject_id/objectives/',
  ID_SUBJECT_OBJECTIVES_REORDER: '/api/academic/instructional-design/subjects/:subject_id/objectives/reorder',
  ID_OBJECTIVES_UPDATE: '/api/academic/instructional-design/objectives/:objective_id',
  ID_OBJECTIVES_DELETE: '/api/academic/instructional-design/objectives/:objective_id',
  ID_FRAMEWORKS_LIST: '/api/academic/instructional-design/frameworks/',
  ID_FRAMEWORKS_CREATE: '/api/academic/instructional-design/frameworks/',
  ID_FRAMEWORKS_DETAIL: '/api/academic/instructional-design/frameworks/:framework_id',
  ID_FRAMEWORKS_UPDATE: '/api/academic/instructional-design/frameworks/:framework_id',
  ID_FRAMEWORKS_DELETE: '/api/academic/instructional-design/frameworks/:framework_id',
  ID_FRAMEWORK_COMPETENCIES_LIST: '/api/academic/instructional-design/frameworks/:framework_id/competencies/',
  ID_FRAMEWORK_COMPETENCIES_CREATE: '/api/academic/instructional-design/frameworks/:framework_id/competencies/',
  ID_FRAMEWORK_COMPETENCIES_REORDER: '/api/academic/instructional-design/frameworks/:framework_id/competencies/reorder',
  ID_COMPETENCIES_UPDATE: '/api/academic/instructional-design/competencies/:competency_id',
  ID_COMPETENCIES_DELETE: '/api/academic/instructional-design/competencies/:competency_id',

  // ACADEMIC - INSTRUCTIONAL DESIGN GOVERNANCE (Gobernanza Pedagógica)
  ID_GOV_PARAMETERS_LIST: '/api/academic/instructional-design/governance/parameters/',
  ID_GOV_PARAMETERS_DETAIL: '/api/academic/instructional-design/governance/parameters/:param_id',
  ID_GOV_PARAMETERS_RESTORE_DEFAULTS: '/api/academic/instructional-design/governance/parameters/restore-defaults',
  ID_GOV_DISTRIBUTION_LIST: '/api/academic/instructional-design/governance/distribution/',
  ID_GOV_MODES_LIST: '/api/academic/instructional-design/governance/modes/',
  ID_GOV_MODES_DETAIL: '/api/academic/instructional-design/governance/modes/:code',
  ID_GOV_SUBJECT_TYPES_LIST: '/api/academic/instructional-design/governance/subject-types/',
  ID_GOV_SUBJECT_TYPES_DETAIL: '/api/academic/instructional-design/governance/subject-types/:code',
  ID_GOV_DOCUMENTS_LIST: '/api/academic/instructional-design/governance/documents/',
  ID_GOV_DOCUMENTS_DETAIL: '/api/academic/instructional-design/governance/documents/:document_id',
  ID_GOV_DOCUMENTS_PDF: '/api/academic/instructional-design/governance/documents/:document_id/pdf',

  // ============================================
  // SYSTEM - EXPORT (Exportación PDF genérica)
  // ============================================
  EXPORT_TABLE_PDF: '/api/export/table-pdf/',
  EXPORT_GROUPED_PDF: '/api/export/grouped-pdf/',

  // ============================================
  // SYSTEM - NOTIFICATIONS (Notificaciones del usuario)
  // ============================================
  NOTIFICATIONS_LIST: '/api/notifications/',
  NOTIFICATIONS_UNREAD_COUNT: '/api/notifications/unread-count',
  NOTIFICATIONS_MARK_READ: '/api/notifications/:notification_id/read',
  NOTIFICATIONS_MARK_ALL_READ: '/api/notifications/read-all',
  NOTIFICATIONS_DELETE: '/api/notifications/:notification_id',
  NOTIFICATIONS_PREFERENCES: '/api/notifications/preferences/',
  NOTIFICATIONS_STREAM_TOKEN: '/api/notifications/stream-token',
  NOTIFICATIONS_STREAM: '/api/notifications/stream',

  // SYSTEM - NOTIFICATION TRIGGERS (Admin — configuración y logs)
  NOTIFICATION_TRIGGERS_LIST: '/api/admin/notification-triggers/',
  NOTIFICATION_TRIGGERS_CREATE: '/api/admin/notification-triggers/',
  NOTIFICATION_TRIGGERS_UPDATE: '/api/admin/notification-triggers/:trigger_id',
  NOTIFICATION_TRIGGERS_DELETE: '/api/admin/notification-triggers/:trigger_id',
  NOTIFICATION_TRIGGERS_TOGGLE: '/api/admin/notification-triggers/:trigger_id/toggle',
  NOTIFICATION_LOGS_LIST: '/api/admin/notification-logs/',

  // SYSTEM - Email Templates (plantillas transaccionales del sistema)
  SYS_EMAIL_TEMPLATES_LIST: '/api/admin/email-templates/',
  SYS_EMAIL_TEMPLATES_DETAIL: '/api/admin/email-templates/:template_id',
  SYS_EMAIL_TEMPLATES_UPDATE: '/api/admin/email-templates/:template_id',
  SYS_EMAIL_TEMPLATES_RESET: '/api/admin/email-templates/:template_id/reset',
  SYS_EMAIL_TEMPLATES_PREVIEW: '/api/admin/email-templates/preview',
  SYS_EMAIL_TEMPLATES_SEND_TEST: '/api/admin/email-templates/:template_id/send-test',

  // SYSTEM - SCHEDULED MAINTENANCE
  SCHEDULED_MAINTENANCE_LIST: '/api/admin/scheduled-maintenance/',
  SCHEDULED_MAINTENANCE_CREATE: '/api/admin/scheduled-maintenance/',
  SCHEDULED_MAINTENANCE_UPDATE: '/api/admin/scheduled-maintenance/:maintenance_id',
  SCHEDULED_MAINTENANCE_DELETE: '/api/admin/scheduled-maintenance/:maintenance_id',
  SCHEDULED_MAINTENANCE_NOTIFY: '/api/admin/scheduled-maintenance/:maintenance_id/notify',

  // ============================================
  // SYSTEM - CALENDARS (Calendarios Anuales)
  // ============================================
  SYSTEM_CALENDARS_LIST: '/api/system/calendars/',  // Barra final para evitar redirect HTTP en Railway
  SYSTEM_CALENDARS_CREATE: '/api/system/calendars/',  // Barra final para evitar redirect HTTP en Railway
  SYSTEM_CALENDARS_DETAIL: '/api/system/calendars/:year',
  SYSTEM_CALENDARS_FULL: '/api/system/calendars/:year/full',
  SYSTEM_CALENDARS_UPDATE: '/api/system/calendars/:calendario_id',
  SYSTEM_CALENDARS_DELETE: '/api/system/calendars/:calendario_id',

  // ============================================
  // ACADEMIC - PERIODS (Períodos Académicos)
  // ============================================
  PERIODS_LIST: '/api/academic/periods/',  // Barra final para evitar redirect HTTP en Railway
  PERIODS_DETAIL: '/api/academic/periods/:periodo_id',

  // ============================================
  // ACADEMIC - PLANNING (Generaciones/Planificación)
  // ============================================
  PLANNINGS_VALIDATE: '/api/academic/schedules/plannings/validate',
  PLANNINGS_LIST: '/api/academic/schedules/plannings/',  // Barra final para evitar redirect HTTP en Railway
  PLANNINGS_DETAIL: '/api/academic/schedules/plannings/:planning_id',
  PLANNINGS_DETAILED: '/api/academic/schedules/plannings/:planning_id/detailed',
  PLANNINGS_BY_SUBJECT: '/api/academic/schedules/plannings/:planning_id/by-subject',
  PLANNINGS_BY_PROGRAM: '/api/academic/schedules/plannings/:planning_id/by-program',
  PLANNINGS_SUBJECT_ASSIGNMENTS: '/api/academic/schedules/plannings/:planning_id/subject-assignments',
  PLANNINGS_SUBJECT_ASSIGNMENTS_UPDATE: '/api/academic/schedules/plannings/:planning_id/subject-assignments',
  PLANNINGS_REPAIR_PROGRAM_ASSIGNMENTS: '/api/academic/schedules/plannings/:planning_id/repair-program-assignments',

  // ============================================
  // COORDINATION - MY PROGRAMS
  // ============================================
  COORDINATION_MY_PLANNINGS: '/api/coordination/my-programs/plannings/',
  COORDINATION_MY_SUBJECTS: '/api/coordination/my-programs/subjects/',
  // Plan-level: materias del plan con config de temario
  COORDINATION_PLAN_SUBJECTS_CONFIG: '/api/coordination/my-programs/plans/:plan_id/subjects-config/',
  COORDINATION_PLAN_CONFIG: '/api/coordination/my-programs/plans/:plan_id/plan-config',
  COORDINATION_PLAN_TEMARIO_CONFIG: '/api/coordination/my-programs/plans/:plan_id/temario-config',
  COORDINATION_PLAN_GENERATE_TEMARIOS: '/api/coordination/my-programs/plans/:plan_id/generate-temarios',
  COORDINATION_PLAN_GENERATION_STATUS: '/api/coordination/my-programs/plans/:plan_id/generation-status',
  COORDINATION_PLAN_COMPLETE_TEMARIOS: '/api/coordination/my-programs/plans/:plan_id/complete-temarios',
  COORDINATION_PLAN_EXPORT_PDF: '/api/coordination/my-programs/plans/:plan_id/export-temario-pdf',
  COORDINATION_SUBJECT_TOPICS: '/api/coordination/my-programs/subjects/:subject_id/topics/',
  COORDINATION_SUBJECT_TOPICS_GENERATE: '/api/coordination/my-programs/subjects/:subject_id/topics/generate',
  COORDINATION_SUBJECT_TOPIC_UPDATE: '/api/coordination/my-programs/subjects/:subject_id/topics/:topic_id',
  COORDINATION_SUBJECT_TOPICS_REORDER: '/api/coordination/my-programs/subjects/:subject_id/topics/reorder',
  COORDINATION_SUBJECT_TOPICS_EXPORT_CSV: '/api/coordination/my-programs/subjects/:subject_id/topics/export-csv',
  COORDINATION_SUBJECT_TOPICS_IMPORT_CSV: '/api/coordination/my-programs/subjects/:subject_id/topics/import-csv',
  COORDINATION_TOPIC_SESSIONS: '/api/coordination/my-programs/topics/:topic_id/sessions/',
  COORDINATION_TOPIC_SESSIONS_GENERATE: '/api/coordination/my-programs/topics/:topic_id/sessions/generate',
  COORDINATION_TOPIC_SESSION_UPDATE: '/api/coordination/my-programs/topics/:topic_id/sessions/:session_id',

  // COORDINATION - SESSION CONTENT (materiales AI)
  COORDINATION_SESSION_CONTENT: '/api/coordination/my-programs/sessions/:session_id/content/',
  COORDINATION_SESSION_CONTENT_GENERATE: '/api/coordination/my-programs/sessions/:session_id/content/generate',
  COORDINATION_SESSION_CONTENT_UPDATE: '/api/coordination/my-programs/sessions/:session_id/content/:content_id',
  COORDINATION_SESSION_CONTENT_REGENERATE: '/api/coordination/my-programs/sessions/:session_id/content/:content_type/regenerate',

  // COORDINATION - KNOWLEDGE BASES
  COORDINATION_SUBJECT_KB_LIST: '/api/coordination/my-programs/subjects/:subject_id/knowledge-bases/',
  COORDINATION_SUBJECT_KB_CREATE: '/api/coordination/my-programs/subjects/:subject_id/knowledge-bases/',
  COORDINATION_KB_DELETE: '/api/coordination/my-programs/knowledge-bases/:kb_id',

  // ============================================
  // ACADEMIC - TEACHERS (Docentes)
  // ============================================
  TEACHERS_LIST: '/api/academic/teachers/',  // Barra final para evitar redirect HTTP en Railway
  TEACHERS_DETAIL: '/api/academic/teachers/:teacher_id',
  TEACHERS_UPDATE: '/api/academic/teachers/:teacher_id',

  // ============================================
  // ADMIN - SUBMISSIONS (Solicitudes de Registro)
  // ============================================
  ADMIN_SUBMISSIONS_LIST: '/api/admin/submissions/',  // Barra final para evitar redirect HTTP en Railway
  ADMIN_SUBMISSIONS_DETAIL: '/api/admin/submissions/:submission_id',
  ADMIN_SUBMISSIONS_APPROVE: '/api/admin/submissions/:submission_id/approve',
  ADMIN_SUBMISSIONS_REJECT: '/api/admin/submissions/:submission_id/reject',
  ADMIN_SUBMISSIONS_REQUEST_CHANGES: '/api/admin/submissions/:submission_id/request-changes',
  ADMIN_SUBMISSIONS_DEADLINE: '/api/admin/submissions/:submission_id/deadline',

  // ============================================
  // ADMIN - INVITATIONS (Invitaciones)
  // ============================================
  ADMIN_INVITATIONS_LIST: '/api/admin/invitations/',  // Barra final para evitar redirect HTTP en Railway
  ADMIN_INVITATIONS_DETAIL: '/api/admin/invitations/:invitation_id',
  ADMIN_INVITATIONS_STATS: '/api/admin/invitations/stats',
  ADMIN_INVITATIONS_REVOKE: '/api/admin/invitations/:invitation_id/revoke',
  ADMIN_INVITATIONS_RESEND: '/api/admin/invitations/:invitation_id/resend',

  // ============================================
  // ADMIN - ROLES (Roles del Sistema)
  // ============================================
  ADMIN_ROLES_LIST: '/api/admin/roles/',  // Barra final para evitar redirect HTTP en Railway

  // ============================================
  // ADMIN - LIFECYCLE (Ciclo de Vida)
  // ============================================
  ADMIN_ANALYZE_IMPACT: '/api/admin/analyze-impact/:entity_type/:entity_id',
  ADMIN_STATUS_SUMMARY: '/api/admin/status-summary',
  ADMIN_RUN_LIFECYCLE_TRANSITIONS: '/api/admin/run-lifecycle-transitions',

  // ============================================
  // ADMIN - IMPERSONATION (Impersonación de Usuarios)
  // ============================================
  ADMIN_IMPERSONATE_START: '/api/admin/impersonate/:account_id',
  ADMIN_IMPERSONATE_STOP: '/api/admin/impersonate/stop',
  ADMIN_IMPERSONATE_HISTORY: '/api/admin/impersonate/history/',

  // ============================================
  // ADMIN - ORGANIZATIONAL SCOPES (Alcances organizacionales)
  // ============================================
  ADMIN_SCOPES_LIST: '/api/admin/scopes/',
  ADMIN_SCOPES_BY_USER: '/api/admin/scopes/user/:account_id',
  ADMIN_SCOPES_CREATE: '/api/admin/scopes/',
  ADMIN_SCOPES_DELETE: '/api/admin/scopes/:scope_id',

  // ============================================
  // PERMISSIONS (Permisos)
  // ============================================
  PERMISSIONS_ME: '/api/permissions/me',
  PERMISSIONS_BY_ROLE: '/api/permissions/role/:role_id',
  PERMISSIONS_BY_PROFILE: '/api/permissions/profile/:profile_id',
  PERMISSIONS_HIERARCHY: '/api/permissions/hierarchy/:permission_id',
  PERMISSIONS_ASSIGN: '/api/permissions/assign',
  PERMISSIONS_REMOVE: '/api/permissions/remove',
  PERMISSIONS_CUSTOM_UPDATE: '/api/permissions/custom/update',
  PERMISSIONS_CUSTOM_CLEAR: '/api/permissions/custom/clear/:profile_id',
  PERMISSIONS_USER_ACTIVE_PROFILE: '/api/permissions/user/:account_id/active-profile',

  // ============================================
  // PERMISSION PRESETS
  // ============================================
  PRESETS_LIST: '/api/permissions/presets/',
  PRESETS_DETAIL: '/api/permissions/presets/:preset_id',
  PRESETS_SET_PERMISSIONS: '/api/permissions/presets/:preset_id/permissions',
  PRESETS_BY_PROFILE: '/api/permissions/presets/profile/:profile_id',
  PRESETS_SET_PROFILE: '/api/permissions/presets/profile/:profile_id',

  // ============================================
  // REGISTRATIONS (Incorporaciones)
  // ============================================
  REGISTRATIONS_TEMPLATES_LIST: '/api/registrations/templates/',  // Barra final para evitar redirect HTTP en Railway
  REGISTRATIONS_TEMPLATES_DETAIL: '/api/registrations/templates/:template_id',
  REGISTRATIONS_TEMPLATES_PREVIEW: '/api/registrations/templates/:template_id/preview',
  REGISTRATIONS_MANUAL: '/api/registrations/manual',
  REGISTRATIONS_USER_ROLES: '/api/registrations/users/:account_id/roles',
  REGISTRATIONS_USER_ROLES_DELETE: '/api/registrations/users/:account_id/roles/:profile_id',

  // ============================================
  // PUBLIC (Endpoints Públicos)
  // ============================================
  PUBLIC_REGISTER: '/api/public/register',
  PUBLIC_INVITE_VALIDATE: '/api/public/invite/:token',
  PUBLIC_ONBOARDING_RESOLVE: '/api/public/onboarding/resolve',
  PUBLIC_ONBOARDING_START: '/api/public/onboarding/start',
  PUBLIC_ONBOARDING_SUBMISSIONS_CREATE: '/api/public/onboarding/submissions/',
  PUBLIC_GEOGRAPHY_STATES: '/api/public/geography/states/:country_code',
  PUBLIC_PROGRAM_TYPES: '/api/public/program-types',

  // ============================================
  // CATALOGO (Catálogo Público de Programas)
  // ============================================
  PUBLIC_CATALOG_PROGRAMS: '/api/public/catalog/programs',
  PUBLIC_CATALOG_PROGRAM_DETAIL: '/api/public/catalog/programs/:profile_id',
  PUBLIC_CATALOG_PROGRAM_DETAIL_SLUG: '/api/public/catalog/programs/:profile_id/:slug',
  PUBLIC_CATALOG_PROGRAM_BY_PROGRAM: '/api/public/catalog/programs/by-program/:program_id',
  PUBLIC_CATALOG_PROGRAM_BY_PROGRAM_SLUG: '/api/public/catalog/programs/by-program/:program_id/:slug',
  PUBLIC_CATALOG_FILTERS: '/api/public/catalog/filters',

  // ============================================
  // PUBLIC - PRE-REGISTRATION
  // ============================================
  PRE_REGISTRATION_PUBLIC: '/api/public/pre-registration/:token',
  PRE_REGISTRATION_COMPLETE: '/api/public/pre-registration/:token/complete',

  // ============================================
  // PUBLIC - LEADS
  // ============================================
  PUBLIC_LEADS_CREATE: '/api/public/leads/',

  // ============================================
  // PUBLIC - ADMIN (Contenido público)
  // ============================================
  PUBLIC_ADMIN_PROGRAM_PROFILES_LIST: '/api/public/admin/program-profiles/',
  PUBLIC_ADMIN_PROGRAM_PROFILES_CREATE: '/api/public/admin/program-profiles/',
  PUBLIC_ADMIN_PROGRAM_PROFILES_DETAIL: '/api/public/admin/program-profiles/:profile_id',
  PUBLIC_ADMIN_PROGRAM_PROFILES_UPDATE: '/api/public/admin/program-profiles/:profile_id',
  PUBLIC_ADMIN_PROGRAM_PROFILES_UPLOAD_IMAGE: '/api/public/admin/program-profiles/:profile_id/upload-image',
  PUBLIC_ADMIN_PROGRAM_PROFILES_BY_LAUNCH: '/api/public/admin/program-profiles/launch/:launch_id',
  PUBLIC_ADMIN_PROGRAM_PROFILES_EXPORT: '/api/public/admin/program-profiles/launch/:launch_id/export',
  PUBLIC_ADMIN_PROGRAM_PROFILES_IMPORT: '/api/public/admin/program-profiles/launch/:launch_id/import',
  PUBLIC_ADMIN_PROGRAM_PROFILES_BULK_UPLOAD_INIT: '/api/public/admin/program-profiles/bulk-upload/init',
  PUBLIC_ADMIN_PROGRAM_PROFILES_BULK_UPLOAD_COMPLETE: '/api/public/admin/program-profiles/bulk-upload/complete',
  PUBLIC_ADMIN_LEADS_LIST: '/api/public/admin/leads/',
  PUBLIC_ADMIN_LEADS_BY_CAMPAIGN: '/api/public/admin/leads/by-campaign/',
  PUBLIC_ADMIN_LEADS_DETAIL: '/api/public/admin/leads/:lead_id',
  PUBLIC_ADMIN_LEADS_UPDATE: '/api/public/admin/leads/:lead_id',

  // ============================================
  // ONBOARDING
  // ============================================
  ONBOARDING_STATUS: '/api/onboarding/status',
  ONBOARDING_IDENTITY: '/api/onboarding/identity',
  ONBOARDING_TEACHER_DETAILS: '/api/onboarding/teacher-details',
  ONBOARDING_TEACHER_DETAILS_PARTIAL: '/api/onboarding/teacher-details/partial',
  ONBOARDING_ADMIN_DETAILS: '/api/onboarding/admin-details',
  ONBOARDING_ADMIN_DETAILS_PARTIAL: '/api/onboarding/admin-details/partial',
  ONBOARDING_CORPORATE_DETAILS: '/api/onboarding/corporate-details',
  ONBOARDING_CORPORATE_DETAILS_PARTIAL: '/api/onboarding/corporate-details/partial',
  ONBOARDING_CORPORATE_OPERATOR: '/api/onboarding/corporate-operator',
  ONBOARDING_CORPORATE_OPERATOR_PARTIAL: '/api/onboarding/corporate-operator/partial',
  ONBOARDING_STUDENT_DETAILS: '/api/onboarding/student-details',
  ONBOARDING_STUDENT_DETAILS_PARTIAL: '/api/onboarding/student-details/partial',
  ONBOARDING_SET_PROGRAM_CONTEXT: '/api/onboarding/set-program-context',
  ONBOARDING_UPLOAD_DOCUMENT: '/api/onboarding/upload-document',
  ONBOARDING_DELETE_DOCUMENT: '/api/onboarding/delete-document/:document_type',
  ONBOARDING_DOCUMENTS_NOTES: '/api/onboarding/documents-notes',
  ONBOARDING_COMPLETE_DOCUMENTS: '/api/onboarding/complete-documents-step',
  ONBOARDING_SUBMIT: '/api/onboarding/submit',
  ONBOARDING_REPORT_ISSUE: '/api/onboarding/report-issue',

  // ============================================
  // MAINTENANCE (Mantenimiento y Análisis)
  // ============================================
  MAINTENANCE_ANALYZE_USAGE: '/api/maintenance/analyze-usage',

  // Audits
  // ============================================
  // TESTING (Sistema de Testing Playwright)
  // ============================================
  // Playwright
  TESTING_PLAYWRIGHT_DEFINITIONS_LIST: '/api/testing/playwright/definitions/',  // Barra final para evitar redirect HTTP en Railway
  TESTING_PLAYWRIGHT_DEFINITIONS_CREATE: '/api/testing/playwright/definitions/',
  TESTING_PLAYWRIGHT_DEFINITIONS_UPLOAD: '/api/testing/playwright/definitions/upload-spec',
  TESTING_PLAYWRIGHT_DEFINITIONS_EXECUTE: '/api/testing/playwright/definitions/:definition_id/execute',
  TESTING_PLAYWRIGHT_EXECUTIONS_LIST: '/api/testing/playwright/executions/',  // Barra final para evitar redirect HTTP en Railway
  TESTING_PLAYWRIGHT_QUEUE_STATS: '/api/testing/playwright/queue/stats',
  TESTING_PLAYWRIGHT_JOB_STATUS: '/api/testing/playwright/jobs/:job_id/status',
  TESTING_PLAYWRIGHT_CODEGEN_START: '/api/testing/playwright/codegen/start',
  TESTING_PLAYWRIGHT_CODEGEN_SCRIPT: '/api/testing/playwright/codegen/:session_id/script',
  TESTING_PLAYWRIGHT_CODEGEN_UPLOAD: '/api/testing/playwright/codegen/:session_id/upload',
  TESTING_PLAYWRIGHT_CODEGEN_STATUS: '/api/testing/playwright/codegen/:session_id/status',
  TESTING_PLAYWRIGHT_CODEGEN_DELETE: '/api/testing/playwright/codegen/:session_id',

  // ============================================
  // SALES - SCHOLARSHIPS (Becas Propias)
  // ============================================
  SCHOLARSHIPS_LIST: '/api/sales/packages/scholarships/',  // Barra final para evitar redirect HTTP en Railway
  SCHOLARSHIPS_CREATE: '/api/sales/packages/scholarships/',
  SCHOLARSHIPS_DETAIL: '/api/sales/packages/scholarships/:scholarship_id',
  SCHOLARSHIPS_UPDATE: '/api/sales/packages/scholarships/:scholarship_id',
  SCHOLARSHIPS_DELETE: '/api/sales/packages/scholarships/:scholarship_id',

  // SALES - TRADE AGREEMENTS (Convenios)
  TRADE_AGREEMENTS_LIST: '/api/sales/trades/agreements/',  // Barra final para evitar redirect HTTP en Railway
  TRADE_AGREEMENTS_CREATE: '/api/sales/trades/agreements/',
  TRADE_AGREEMENTS_DETAIL: '/api/sales/trades/agreements/:agreement_id',
  TRADE_AGREEMENTS_UPDATE: '/api/sales/trades/agreements/:agreement_id',
  TRADE_AGREEMENTS_DELETE: '/api/sales/trades/agreements/:agreement_id',
  TRADE_AGREEMENTS_OPTIONS: '/api/sales/trades/agreements/options',
  TRADE_DISCOUNT_CONDITION_OPTIONS: '/api/sales/discounts/conditions/options',

  // SALES - PAYMENT SCHEMES (Esquemas de Pago)
  PAYMENT_SCHEMES_LIST: '/api/sales/payment-schemes/',
  PAYMENT_SCHEMES_DETAIL: '/api/sales/payment-schemes/:scheme_id',
  PAYMENT_SCHEMES_UPDATE: '/api/sales/payment-schemes/:scheme_id',

  // SALES - PRICING LISTS
  PRICING_LISTS_LIST: '/api/sales/pricing/',
  PRICING_LISTS_DETAIL: '/api/sales/pricing/:list_id',
  PRICING_LISTS_CREATE: '/api/sales/pricing/',
  PRICING_LISTS_UPDATE: '/api/sales/pricing/:list_id',
  PRICING_LISTS_PUBLISH: '/api/sales/pricing/:list_id/publish',
  PRICING_LISTS_CLONE: '/api/sales/pricing/:list_id/clone',
  PRICING_LIST_ACTIVE: '/api/sales/pricing/active',
  PRICING_CALCULATE_PRICE: '/api/sales/pricing/calculate-price',

  // SALES - PRICING STATIC (por programa)
  PRICING_STATIC_LIST: '/api/sales/pricing-static/',
  PRICING_STATIC_CREATE: '/api/sales/pricing-static/',
  PRICING_STATIC_UPDATE: '/api/sales/pricing-static/:price_id',
  PRICING_STATIC_DELETE: '/api/sales/pricing-static/:price_id',
  PRICING_STATIC_HISTORY: '/api/sales/pricing-static/:price_id/history',

  // SALES - TRADE DISCOUNTS (Descuentos)
  TRADE_DISCOUNTS_LIST: '/api/sales/discounts/',  // Barra final para evitar redirect HTTP en Railway
  TRADE_DISCOUNTS_CREATE: '/api/sales/discounts/',
  TRADE_DISCOUNTS_DETAIL: '/api/sales/discounts/:discount_id',
  TRADE_DISCOUNTS_UPDATE: '/api/sales/discounts/:discount_id',
  TRADE_DISCOUNTS_DELETE: '/api/sales/discounts/:discount_id',

  // SALES - TRADE PROMOTIONS (Promociones por Convenio)
  TRADE_PROMOTIONS_LIST: '/api/sales/trades/promotions/',
  TRADE_PROMOTIONS_CREATE: '/api/sales/trades/promotions/',
  TRADE_PROMOTIONS_DETAIL: '/api/sales/trades/promotions/:promotion_id',
  TRADE_PROMOTIONS_UPDATE: '/api/sales/trades/promotions/:promotion_id',
  TRADE_PROMOTIONS_DELETE: '/api/sales/trades/promotions/:promotion_id',

  // SALES - LAUNCHES (Publicación de Programas)
  SALES_LAUNCHES_LIST: '/api/sales/launches/',
  SALES_LAUNCHES_DETAIL: '/api/sales/launches/:launch_id',
  SALES_LAUNCHES_CREATE: '/api/sales/launches/',
  SALES_LAUNCHES_UPDATE: '/api/sales/launches/:launch_id',
  SALES_LAUNCHES_PUBLISH: '/api/sales/launches/:launch_id/publish',
  SALES_LAUNCHES_CLOSE: '/api/sales/launches/:launch_id/close',
  SALES_LAUNCHES_PAUSE: '/api/sales/launches/:launch_id/pause',
  SALES_LAUNCHES_RESUME: '/api/sales/launches/:launch_id/resume',
  SALES_LAUNCHES_DELETE: '/api/sales/launches/:launch_id',
  SALES_LAUNCHES_ADD_INCENTIVE: '/api/sales/launches/programs/:launch_program_id/incentives',
  SALES_LAUNCHES_DELETE_INCENTIVE: '/api/sales/launches/incentives/:incentive_id',
  SALES_LAUNCHES_ADD_PAYMENT: '/api/sales/launches/programs/:launch_program_id/payments',
  SALES_LAUNCHES_DELETE_PAYMENT: '/api/sales/launches/payments/:payment_id',
  SALES_LAUNCHES_CURRENCIES: '/api/sales/launches/currencies',
  SALES_LAUNCHES_PREVIEW_PROGRAMS: '/api/sales/launches/preview/programs',
  SALES_LAUNCHES_PREVIEW_INCENTIVES: '/api/sales/launches/preview/incentives',

  // ============================================
  // HR - DIRECTORY (Directorio de Personal)
  // ============================================
  HR_DIRECTORY_LIST: '/api/hr/directory/',
  HR_DIRECTORY_DETAIL: '/api/hr/directory/:person_id',
  HR_DIRECTORY_DOCUMENTS: '/api/hr/directory/:person_id/documents/',
  HR_DIRECTORY_DOCUMENT_DOWNLOAD: '/api/hr/directory/:person_id/documents/:doc_id/download',
  HR_DIRECTORY_DOCUMENT_DELETE: '/api/hr/directory/:person_id/documents/:doc_id',
  HR_DIRECTORY_DOC_REQUIREMENTS: '/api/hr/directory/:person_id/document-requirements',

  // ============================================
  // HR - ORGCHART (Organigrama Institucional)
  // ============================================
  HR_ORGCHART_TREE: '/api/hr/orgchart/tree',
  HR_ORGCHART_UNITS_LIST: '/api/hr/orgchart/units/',
  HR_ORGCHART_UNITS_DETAIL: '/api/hr/orgchart/units/:unit_id',
  HR_ORGCHART_POSITIONS_LIST: '/api/hr/orgchart/positions/',
  HR_ORGCHART_POSITIONS_DETAIL: '/api/hr/orgchart/positions/:position_id',
  HR_ORGCHART_POSITION_HOLDERS: '/api/hr/orgchart/positions/:position_id/holders/',
  HR_ORGCHART_POSITION_HOLDER_DELETE: '/api/hr/orgchart/positions/:position_id/holders/:holder_id',
  HR_ORGCHART_SYNC: '/api/hr/orgchart/sync-from-directory',

  // ============================================
  // SYSTEM - Procedures (Procedimientos organizacionales)
  // ============================================
  SYSTEM_PROCEDURES_LIST: '/api/system/procedures/',
  SYSTEM_PROCEDURES_DETAIL: '/api/system/procedures/:procedure_id',
  SYSTEM_PROCEDURES_CATEGORIES: '/api/system/procedures/meta/categories/',
  SYSTEM_PROCEDURES_ADD_STEP: '/api/system/procedures/:procedure_id/steps/',
  SYSTEM_PROCEDURES_UPDATE_STEP: '/api/system/procedures/:procedure_id/steps/:process_id',
  SYSTEM_PROCEDURES_REMOVE_STEP: '/api/system/procedures/:procedure_id/steps/:process_id',
  SYSTEM_PROCEDURES_LAYOUT: '/api/system/procedures/:procedure_id/layout/',

  // Procedure Instances (ejecuciones de procedimientos)
  SYSTEM_PROC_INSTANCES_LIST: '/api/system/procedure-instances/',
  SYSTEM_PROC_INSTANCES_DETAIL: '/api/system/procedure-instances/:instance_id',
  SYSTEM_PROC_INSTANCES_UPDATE_STEP: '/api/system/procedure-instances/:instance_id/steps/:step_log_id',
  SYSTEM_PROC_INSTANCES_MY_TASKS: '/api/system/procedure-instances/my-tasks/',
  SYSTEM_PROC_INSTANCES_MY_TASKS_COUNT: '/api/system/procedure-instances/my-tasks/count/',
  SYSTEM_PROC_INSTANCES_DASHBOARD: '/api/system/procedure-instances/dashboard/stats/',

  // ============================================
  // HR - RACI (Matriz de Responsabilidades)
  // ============================================
  HR_RACI_PROCESSES: '/api/hr/raci/processes/',
  HR_RACI_PROCESS_DETAIL: '/api/hr/raci/processes/:process_id',
  HR_RACI_MATRIX: '/api/hr/raci/matrix/',
  HR_RACI_ASSIGNMENTS: '/api/hr/raci/assignments/',
  HR_RACI_ASSIGNMENT_DELETE: '/api/hr/raci/assignments/:process_id/:position_id',
  HR_RACI_BULK_IMPORT: '/api/hr/raci/bulk-import/',

  // ============================================
  // HR - COMPETENCIES (Matriz de Competencias)
  // ============================================
  HR_COMPETENCIES_LIST: '/api/hr/competencies/',
  HR_COMPETENCIES_DETAIL: '/api/hr/competencies/:competency_id',
  HR_COMPETENCIES_MATRIX: '/api/hr/competencies/matrix/',
  HR_COMPETENCIES_ASSIGNMENTS: '/api/hr/competencies/assignments/',
  HR_COMPETENCIES_BULK_IMPORT: '/api/hr/competencies/bulk-import/',

  // ============================================
  // HR - POSITION DOCUMENTS (Documentos de Puestos - Minio)
  // ============================================
  HR_POSITION_DOCUMENTS_LIST: '/api/hr/position-documents/',
  HR_POSITION_DOCUMENT_DOWNLOAD: '/api/hr/position-documents/:doc_id/download',
  HR_POSITION_DOCUMENT_DELETE: '/api/hr/position-documents/:doc_id',
  HR_POSITION_DOCUMENTS_SUMMARY: '/api/hr/position-documents/summary/',
  HR_POSITION_DOCUMENT_GENERATE: '/api/hr/position-documents/generate/:position_id',

  // ============================================
  // HR - SALARY BANDS (Tabulador Salarial)
  // ============================================
  HR_SALARY_BANDS_LIST: '/api/hr/salary-bands/',
  HR_SALARY_BANDS_DETAIL: '/api/hr/salary-bands/:band_id',

  // ============================================
  // SYSTEM - INTEGRATIONS (Proveedor de Identidad y Email)
  // ============================================
  INTEGRATIONS_IDENTITY: '/api/system/integrations/identity-provider/',
  INTEGRATIONS_IDENTITY_TEST: '/api/system/integrations/identity-provider/test',
  INTEGRATIONS_EMAIL: '/api/system/integrations/email-config/',
  INTEGRATIONS_EMAIL_TEST: '/api/system/integrations/email-config/test',
  AUTH_CONFIG: '/api/auth/config',

  // ============================================
  // LMS - SMARTTUTOR (Microservicio AI Tutor)
  // ============================================
  // Health
  LMS_HEALTH: '/api/lms/health',

  // Knowledge Bases (Teacher)
  LMS_KB_LIST: '/api/lms/knowledge-bases/',
  LMS_KB_CREATE: '/api/lms/knowledge-bases/',
  LMS_KB_DETAIL: '/api/lms/knowledge-bases/:kb_id',
  LMS_KB_UPDATE: '/api/lms/knowledge-bases/:kb_id',
  LMS_KB_DELETE: '/api/lms/knowledge-bases/:kb_id',

  // Documents (Teacher)
  LMS_DOCUMENTS_LIST: '/api/lms/knowledge-bases/:kb_id/documents/',
  LMS_DOCUMENTS_UPLOAD: '/api/lms/knowledge-bases/:kb_id/documents/',
  LMS_DOCUMENTS_DELETE: '/api/lms/documents/:doc_id',
  LMS_DOCUMENTS_ADD_URL: '/api/lms/knowledge-bases/:kb_id/urls/',

  // Sessions (Teacher)
  LMS_SESSIONS_LIST: '/api/lms/sessions/',
  LMS_SESSIONS_CREATE: '/api/lms/sessions/',
  LMS_SESSIONS_DETAIL: '/api/lms/sessions/:session_id',
  LMS_SESSIONS_UPDATE: '/api/lms/sessions/:session_id',
  LMS_SESSIONS_DELETE: '/api/lms/sessions/:session_id',
  LMS_SESSIONS_PUBLISH: '/api/lms/sessions/:session_id/publish',
  LMS_SESSIONS_ARCHIVE: '/api/lms/sessions/:session_id/archive',
  LMS_SESSIONS_CONTENT: '/api/lms/sessions/:session_id/content/',
  LMS_SESSIONS_CONTENT_GENERATE: '/api/lms/sessions/:session_id/content/generate',
  LMS_SESSIONS_CONTENT_TYPE: '/api/lms/sessions/:session_id/content/:content_type',
  LMS_SESSIONS_CONTENT_UPDATE: '/api/lms/sessions/:session_id/content/:content_id',
  LMS_SESSIONS_CONTENT_BLANK: '/api/lms/sessions/:session_id/content/blank',
  LMS_SESSIONS_CONTENT_REGENERATE: '/api/lms/sessions/:session_id/content/:content_type/regenerate',

  // Teacher Progress
  LMS_SESSION_STUDENTS: '/api/lms/sessions/:session_id/students/',
  LMS_SESSION_STUDENT_DETAIL: '/api/lms/sessions/:session_id/students/:student_session_id',
  LMS_SESSION_STATS: '/api/lms/sessions/:session_id/stats',

  // Student Sessions
  LMS_STUDENT_SESSIONS_LIST: '/api/lms/student/sessions/',
  LMS_STUDENT_SESSION_START: '/api/lms/student/sessions/:session_id/start',
  LMS_STUDENT_SESSION_DETAIL: '/api/lms/student/sessions/:student_session_id',
  LMS_STUDENT_SESSION_PERSONA: '/api/lms/student/sessions/:student_session_id/persona',
  LMS_STUDENT_SESSION_SUMMARY: '/api/lms/student/sessions/:student_session_id/summary',
  LMS_STUDENT_SESSION_SUMMARY_PDF: '/api/lms/student/sessions/:student_session_id/summary/pdf',
  LMS_STUDENT_SESSION_NEXT: '/api/lms/student/sessions/:student_session_id/next',

  // Costs (admin only — Contabilidad y Finanzas)
  LMS_COSTS_SUMMARY: '/api/lms/costs/summary',
  LMS_COSTS_DETAIL: '/api/lms/costs/detail',

  // Chat
  LMS_CHAT: '/api/lms/chat',
  LMS_CHAT_STREAM: '/api/lms/chat/stream',
  LMS_CHAT_HISTORY: '/api/lms/chat/:student_session_id/history',

  // Evaluations
  LMS_EVALUATION_DETAIL: '/api/lms/evaluations/:evaluation_id',
  LMS_EVALUATIONS_BY_SESSION: '/api/lms/evaluations/session/:student_session_id/',

  // Analytics (Phase 3)
  LMS_ANALYTICS_TEACHER_OVERVIEW: '/api/lms/analytics/teacher/overview',
  LMS_ANALYTICS_TEACHER_TRENDS: '/api/lms/analytics/teacher/trends',
  LMS_ANALYTICS_TEACHER_STRUGGLE_TOPICS: '/api/lms/analytics/teacher/struggle-topics',
  LMS_ANALYTICS_TEACHER_SESSION_EXPORT: '/api/lms/analytics/teacher/sessions/:session_id/export',
  LMS_ANALYTICS_TEACHER_EXPORT: '/api/lms/analytics/teacher/export',
  LMS_ANALYTICS_STUDENT_PROGRESS: '/api/lms/analytics/student/progress',
  LMS_ANALYTICS_STUDENT_EXPORT: '/api/lms/analytics/student/export',

  // Materias v2 (SmartTutor)
  LMS_MATERIAS_LIST: '/api/lms/materias/',
  LMS_MATERIAS_CREATE: '/api/lms/materias/',
  LMS_MATERIA_DETAIL: '/api/lms/materias/:materia_id',
  LMS_MATERIA_UPDATE: '/api/lms/materias/:materia_id',
  LMS_MATERIA_PROPEDEUTICO: '/api/lms/materias/:materia_id/propedeutico',
  LMS_MATERIA_SEMANAS: '/api/lms/materias/:materia_id/semanas',
  LMS_MATERIA_SEMANA_DETAIL: '/api/lms/materias/:materia_id/semanas/:week',
  LMS_MATERIA_SYNC_SESSIONS: '/api/lms/materias/:materia_id/sync-sessions',
  LMS_MATERIA_ASSESS: '/api/lms/materias/:materia_id/assess',
  LMS_MATERIA_SESSIONS: '/api/lms/materias/:materia_id/sessions',
  LMS_MATERIA_SESSION_DETAIL: '/api/lms/materias/:materia_id/sessions/:session_id',

  // Personas IA (SmartTutor)
  LMS_PERSONAS_LIST: '/api/lms/personas/',
  LMS_PERSONA_DETAIL: '/api/lms/personas/:persona_id',
  LMS_PERSONA_UPDATE: '/api/lms/personas/:persona_id',

  // Student Session — materia-aware (SmartTutor)
  LMS_STUDENT_SESSION_MATERIA_START: '/api/lms/student/sessions/materia/start',
  LMS_STUDENT_MATERIAS_PROGRESS: '/api/lms/student/materias/progress',

  // Generation Pipeline (SmartTutor)
  LMS_GENERATION_START: '/api/lms/generation/start',
  LMS_GENERATION_TEMARIO: '/api/lms/generation/temario',
  LMS_GENERATION_TEMARIO_UPDATE: '/api/lms/generation/temario',
  LMS_GENERATION_JOB_PROGRESS: '/api/lms/generation/jobs/:job_id/progress',
  LMS_GENERATION_STEP_RETRY: '/api/lms/generation/steps/:step_id/retry',
  LMS_GENERATION_JOB_CANCEL: '/api/lms/generation/jobs/:job_id/cancel',
  LMS_GENERATION_REGENERATE: '/api/lms/generation/regenerate',

  // AI Error Log (Mantenimiento → Errores AI)
  LMS_AI_ERRORS_LIST: '/api/lms/ai-errors/',
  LMS_AI_ERRORS_SUMMARY: '/api/lms/ai-errors/summary',

  // CIL — Cognitive Intelligence Layer (Dashboard Docente)
  LMS_CIL_MATERIA_STUDENTS: '/api/lms/cil/materia/:materia_id/students',
  LMS_CIL_MATERIA_ALERTS: '/api/lms/cil/materia/:materia_id/alerts',
  LMS_CIL_STUDENT_STATE: '/api/lms/cil/state/:student_id',

  // ============================================
  // ROADMAP - Control de Proyecto
  // ============================================
  ROADMAP_DASHBOARD: '/api/system/roadmap/dashboard/',
  ROADMAP_AREAS_LIST: '/api/system/roadmap/areas/',
  ROADMAP_AREA_DETAIL: '/api/system/roadmap/areas/:area_code/',
  ROADMAP_PROCEDURES_LIST: '/api/system/roadmap/procedures/',
  ROADMAP_PROCEDURE_UPDATE: '/api/system/roadmap/procedures/:procedure_id',
  ROADMAP_ITEMS_LIST: '/api/system/roadmap/items/',
  ROADMAP_ITEMS_CREATE: '/api/system/roadmap/items/',
  ROADMAP_ITEM_UPDATE: '/api/system/roadmap/items/:item_id',
  ROADMAP_ITEM_DELETE: '/api/system/roadmap/items/:item_id',
  ROADMAP_DEPENDENCIES_LIST: '/api/system/roadmap/dependencies/',

  // SMK_* endpoints removed in Sprint 6 — smarthub-marketing retired.
  // All CRM endpoints now go through /api/growth/* proxy → stratos-growth.

  // ============================================
  // GROWTH CRM — via backend-proxy /api/growth/* → stratos-growth
  // ============================================
  // Contacts
  GROWTH_CONTACTS_LIST: '/api/growth/contacts',
  GROWTH_CONTACTS_CREATE: '/api/growth/contacts',
  GROWTH_CONTACTS_DETAIL: '/api/growth/contacts/:contact_id',
  GROWTH_CONTACTS_UPDATE: '/api/growth/contacts/:contact_id',
  GROWTH_CONTACTS_DELETE: '/api/growth/contacts/:contact_id',

  // Companies
  GROWTH_COMPANIES_LIST: '/api/growth/companies',
  GROWTH_COMPANIES_CREATE: '/api/growth/companies',
  GROWTH_COMPANIES_DETAIL: '/api/growth/companies/:company_id',
  GROWTH_COMPANIES_UPDATE: '/api/growth/companies/:company_id',
  GROWTH_COMPANIES_DELETE: '/api/growth/companies/:company_id',

  // Pipelines
  GROWTH_PIPELINES_LIST: '/api/growth/pipelines',
  GROWTH_PIPELINES_CREATE: '/api/growth/pipelines',
  GROWTH_PIPELINES_DETAIL: '/api/growth/pipelines/:pipeline_id',
  GROWTH_PIPELINES_UPDATE: '/api/growth/pipelines/:pipeline_id',
  GROWTH_PIPELINES_DELETE: '/api/growth/pipelines/:pipeline_id',

  // Deals
  GROWTH_DEALS_LIST: '/api/growth/deals',
  GROWTH_DEALS_CREATE: '/api/growth/deals',
  GROWTH_DEALS_DETAIL: '/api/growth/deals/:deal_id',
  GROWTH_DEALS_UPDATE: '/api/growth/deals/:deal_id',
  GROWTH_DEALS_DELETE: '/api/growth/deals/:deal_id',

  // Deal Activities
  GROWTH_DEAL_ACTIVITIES_LIST: '/api/growth/deals/:deal_id/activities',
  GROWTH_DEAL_ACTIVITIES_CREATE: '/api/growth/deals/:deal_id/activities',

  // Landing Pages (Sprint 5)
  GROWTH_LANDING_PAGES_LIST: '/api/growth/landing-pages',
  GROWTH_LANDING_PAGES_CREATE: '/api/growth/landing-pages',
  GROWTH_LANDING_PAGES_DETAIL: '/api/growth/landing-pages/:landing_id',
  GROWTH_LANDING_PAGES_UPDATE: '/api/growth/landing-pages/:landing_id',
  GROWTH_LANDING_PAGES_DELETE: '/api/growth/landing-pages/:landing_id',

  // Campaigns (Sprint 5 — CRUD only; engine endpoints in Sprint 8)
  GROWTH_CAMPAIGNS_LIST: '/api/growth/campaigns',
  GROWTH_CAMPAIGNS_CREATE: '/api/growth/campaigns',
  GROWTH_CAMPAIGNS_DETAIL: '/api/growth/campaigns/:campaign_id',
  GROWTH_CAMPAIGNS_UPDATE: '/api/growth/campaigns/:campaign_id',
  GROWTH_CAMPAIGNS_DELETE: '/api/growth/campaigns/:campaign_id',

  // Analytics
  GROWTH_ANALYTICS_FUNNEL: '/api/growth/analytics/funnel',

  // ============================================
  // ENROLLMENT (Student self-enrollment)
  // ============================================
  ENROLLMENT_PROGRAMS_LIST: '/api/enrollment/programs/',
  ENROLLMENT_CALCULATE: '/api/enrollment/calculate/',
  ENROLLMENT_SUBSCRIBE: '/api/enrollment/subscribe/',
  ENROLLMENT_MY_STATUS: '/api/enrollment/my-status/',
  ENROLLMENT_PAY_INSTALLMENT: '/api/enrollment/pay/:payment_id',
  ENROLLMENT_SET_PAYMENT_MODE: '/api/enrollment/set-payment-mode/:application_id',

  // Admin Enrollment (becas + approval)
  ENROLLMENT_ADMIN_APPROVE: '/api/admin/enrollment/approve/',
  ENROLLMENT_ADMIN_APPLICATIONS_LIST: '/api/admin/enrollment/applications/',
  ENROLLMENT_ADMIN_APPLICATION_DETAIL: '/api/admin/enrollment/applications/:application_id',

  // ============================================
  // STUDENT PORTAL
  // ============================================
  STUDENT_MY_PROFILE: '/api/student/my-profile/',
  STUDENT_MY_PROFILE_PHOTO: '/api/student/my-profile/photo',
  STUDENT_MY_PROFILE_PERSONAL_INFO: '/api/student/my-profile/personal-info',
  STUDENT_MY_PROFILE_EMERGENCY_CONTACT: '/api/student/my-profile/emergency-contact',
  STUDENT_MY_PROFILE_OCCUPATION: '/api/student/my-profile/occupation',
  STUDENT_MY_PROGRAMS: '/api/student/my-programs/',
  STUDENT_MY_WEEK: '/api/student/my-week/',
  STUDENT_HOLIDAYS: '/api/student/holidays/:year',

  // PDP — Plan de Desarrollo Personal
  STUDENT_PDP_DETAIL: '/api/student/pdp/:enrollment_id',
  STUDENT_PDP_QUESTIONNAIRE: '/api/student/pdp/:enrollment_id/questionnaire',
  STUDENT_PDP_INITIAL_ASSESSMENT: '/api/student/pdp/:enrollment_id/initial-assessment',
  STUDENT_PDP_FINAL_ASSESSMENT: '/api/student/pdp/:enrollment_id/final-assessment',
  STUDENT_PDP_PLAN_UPDATE: '/api/student/pdp/:enrollment_id/plan',

  // ============================================
  // TEACHER PORTAL
  // ============================================
  TEACHER_MY_PROFILE: '/api/teacher/my-profile/',

  // ============================================
  // CORPORATION PORTAL
  // ============================================
  CORPORATION_MY_PROFILE: '/api/corporation/my-profile/',
};

/**
 * Exportar también agrupados por dominio para facilitar navegación
 */
export const ENDPOINTS_BY_DOMAIN = {
  auth: {
    register: API_ENDPOINTS.AUTH_REGISTER,
    login: API_ENDPOINTS.AUTH_LOGIN,
    google: API_ENDPOINTS.AUTH_GOOGLE,
    logout: API_ENDPOINTS.AUTH_LOGOUT,
    refresh: API_ENDPOINTS.AUTH_REFRESH,
    me: API_ENDPOINTS.AUTH_ME,
    myPermissions: API_ENDPOINTS.AUTH_MY_PERMISSIONS,
    passwordReset: API_ENDPOINTS.AUTH_PASSWORD_RESET,
    passwordResetConfirm: API_ENDPOINTS.AUTH_PASSWORD_RESET_CONFIRM,
  },

  users: {
    list: API_ENDPOINTS.USERS_LIST,
    detail: API_ENDPOINTS.USERS_DETAIL,
    validateIdentity: API_ENDPOINTS.USERS_VALIDATE_IDENTITY,
    status: API_ENDPOINTS.USERS_STATUS,
    profiles: API_ENDPOINTS.USERS_PROFILES,
    profilesDelete: API_ENDPOINTS.USERS_PROFILES_DELETE,
    activity: API_ENDPOINTS.USERS_ACTIVITY,
  },

  plans: {
    list: API_ENDPOINTS.PLANS_LIST,
    create: API_ENDPOINTS.PLANS_CREATE,
    detail: API_ENDPOINTS.PLANS_DETAIL,
    update: API_ENDPOINTS.PLANS_UPDATE,
    delete: API_ENDPOINTS.PLANS_DELETE,
    structure: API_ENDPOINTS.PLANS_STRUCTURE,
    validate: API_ENDPOINTS.PLANS_VALIDATE,
    builder: API_ENDPOINTS.PLANS_BUILDER,
    tree: API_ENDPOINTS.PLANS_TREE,
    programUpdate: API_ENDPOINTS.PLANS_PROGRAM_UPDATE,
    history: API_ENDPOINTS.PLANS_HISTORY,
    compare: API_ENDPOINTS.PLANS_COMPARE,
    restore: API_ENDPOINTS.PLANS_RESTORE,
    deleteMultiple: API_ENDPOINTS.PLANS_DELETE_MULTIPLE,
    validateExisting: API_ENDPOINTS.PLANS_VALIDATE_EXISTING,
  },

  // Templates
  templates: {
    list: API_ENDPOINTS.TEMPLATES_LIST,
    detail: API_ENDPOINTS.TEMPLATES_DETAIL,
    create: API_ENDPOINTS.TEMPLATES_CREATE,
    update: API_ENDPOINTS.TEMPLATES_UPDATE,
    delete: API_ENDPOINTS.TEMPLATES_DELETE,
    visibility: API_ENDPOINTS.TEMPLATES_VISIBILITY,
    builder: API_ENDPOINTS.PLANS_BUILDER,
  },

  programs: {
    list: API_ENDPOINTS.PROGRAMS_LIST,
    types: API_ENDPOINTS.PROGRAMS_TYPES,
    totalHours: API_ENDPOINTS.PROGRAMS_TOTAL_HOURS,
    recalculateAllHours: API_ENDPOINTS.PROGRAMS_RECALCULATE_ALL_HOURS,
    recalculateSubjectHours: API_ENDPOINTS.PROGRAMS_RECALCULATE_SUBJECT_HOURS,
    recalculateAllSubjectHours: API_ENDPOINTS.PROGRAMS_RECALCULATE_ALL_SUBJECT_HOURS,
  },

  subjects: {
    list: API_ENDPOINTS.SUBJECTS_LIST,
    create: API_ENDPOINTS.SUBJECTS_CREATE,
    detail: API_ENDPOINTS.SUBJECTS_DETAIL,
    update: API_ENDPOINTS.SUBJECTS_UPDATE,
    delete: API_ENDPOINTS.SUBJECTS_DELETE,
    types: API_ENDPOINTS.SUBJECTS_TYPES,
    hours: API_ENDPOINTS.SUBJECTS_HOURS,
    hourRules: API_ENDPOINTS.SUBJECTS_HOUR_RULES,
  },

  projects: {
    list: API_ENDPOINTS.PROJECTS_LIST,
    detail: API_ENDPOINTS.PROJECTS_DETAIL,
  },

  resources: {
    centers: {
      list: API_ENDPOINTS.CENTERS_LIST,
      create: API_ENDPOINTS.CENTERS_CREATE,
      detail: API_ENDPOINTS.CENTERS_DETAIL,
      update: API_ENDPOINTS.CENTERS_UPDATE,
      delete: API_ENDPOINTS.CENTERS_DELETE,
    },
    campus: {
      list: API_ENDPOINTS.CAMPUS_LIST,
      create: API_ENDPOINTS.CAMPUS_CREATE,
      detail: API_ENDPOINTS.CAMPUS_DETAIL,
      update: API_ENDPOINTS.CAMPUS_UPDATE,
      delete: API_ENDPOINTS.CAMPUS_DELETE,
    },
    classrooms: {
      list: API_ENDPOINTS.CLASSROOMS_LIST,
      create: API_ENDPOINTS.CLASSROOMS_CREATE,
      detail: API_ENDPOINTS.CLASSROOMS_DETAIL,
      update: API_ENDPOINTS.CLASSROOMS_UPDATE,
      delete: API_ENDPOINTS.CLASSROOMS_DELETE,
    },
  },

  system: {
    calendars: {
      list: API_ENDPOINTS.SYSTEM_CALENDARS_LIST,
      detail: API_ENDPOINTS.SYSTEM_CALENDARS_DETAIL,
      full: API_ENDPOINTS.SYSTEM_CALENDARS_FULL,
    },
    integrations: {
      identity: API_ENDPOINTS.INTEGRATIONS_IDENTITY,
      identityTest: API_ENDPOINTS.INTEGRATIONS_IDENTITY_TEST,
      email: API_ENDPOINTS.INTEGRATIONS_EMAIL,
      emailTest: API_ENDPOINTS.INTEGRATIONS_EMAIL_TEST,
      authConfig: API_ENDPOINTS.AUTH_CONFIG,
    },
  },

  generaciones: {
    periods: {
      list: API_ENDPOINTS.PERIODS_LIST,
      detail: API_ENDPOINTS.PERIODS_DETAIL,
    },
    generations: {
      validate: API_ENDPOINTS.PLANNINGS_VALIDATE,
      list: API_ENDPOINTS.PLANNINGS_LIST,
      detail: API_ENDPOINTS.PLANNINGS_DETAIL,
      detailed: API_ENDPOINTS.PLANNINGS_DETAILED,
      bySubject: API_ENDPOINTS.PLANNINGS_BY_SUBJECT,
      byProgram: API_ENDPOINTS.PLANNINGS_BY_PROGRAM,
      subjectAssignments: API_ENDPOINTS.PLANNINGS_SUBJECT_ASSIGNMENTS,
      updateSubjectAssignments: API_ENDPOINTS.PLANNINGS_SUBJECT_ASSIGNMENTS_UPDATE,
      repairProgramAssignments: API_ENDPOINTS.PLANNINGS_REPAIR_PROGRAM_ASSIGNMENTS,
    },
  },

  admin: {
    submissions: {
      list: API_ENDPOINTS.ADMIN_SUBMISSIONS_LIST,
      detail: API_ENDPOINTS.ADMIN_SUBMISSIONS_DETAIL,
      approve: API_ENDPOINTS.ADMIN_SUBMISSIONS_APPROVE,
      reject: API_ENDPOINTS.ADMIN_SUBMISSIONS_REJECT,
      requestChanges: API_ENDPOINTS.ADMIN_SUBMISSIONS_REQUEST_CHANGES,
      deadline: API_ENDPOINTS.ADMIN_SUBMISSIONS_DEADLINE,
    },
    invitations: {
      list: API_ENDPOINTS.ADMIN_INVITATIONS_LIST,
      detail: API_ENDPOINTS.ADMIN_INVITATIONS_DETAIL,
      stats: API_ENDPOINTS.ADMIN_INVITATIONS_STATS,
      revoke: API_ENDPOINTS.ADMIN_INVITATIONS_REVOKE,
    },
    roles: {
      list: API_ENDPOINTS.ADMIN_ROLES_LIST,
    },
    lifecycle: {
      analyzeImpact: API_ENDPOINTS.ADMIN_ANALYZE_IMPACT,
      statusSummary: API_ENDPOINTS.ADMIN_STATUS_SUMMARY,
      runTransitions: API_ENDPOINTS.ADMIN_RUN_LIFECYCLE_TRANSITIONS,
    },
  },

  permissions: {
    list: API_ENDPOINTS.PERMISSIONS_LIST,
    me: API_ENDPOINTS.PERMISSIONS_ME,
    byRole: API_ENDPOINTS.PERMISSIONS_BY_ROLE,
    byProfile: API_ENDPOINTS.PERMISSIONS_BY_PROFILE,
    hierarchy: API_ENDPOINTS.PERMISSIONS_HIERARCHY,
    assign: API_ENDPOINTS.PERMISSIONS_ASSIGN,
    remove: API_ENDPOINTS.PERMISSIONS_REMOVE,
    customUpdate: API_ENDPOINTS.PERMISSIONS_CUSTOM_UPDATE,
    customClear: API_ENDPOINTS.PERMISSIONS_CUSTOM_CLEAR,
    userActiveProfile: API_ENDPOINTS.PERMISSIONS_USER_ACTIVE_PROFILE,
    presets: {
      list: API_ENDPOINTS.PRESETS_LIST,
      detail: API_ENDPOINTS.PRESETS_DETAIL,
      setPermissions: API_ENDPOINTS.PRESETS_SET_PERMISSIONS,
      byProfile: API_ENDPOINTS.PRESETS_BY_PROFILE,
      setProfile: API_ENDPOINTS.PRESETS_SET_PROFILE,
    },
  },

  registrations: {
    templates: {
      list: API_ENDPOINTS.REGISTRATIONS_TEMPLATES_LIST,
      detail: API_ENDPOINTS.REGISTRATIONS_TEMPLATES_DETAIL,
    },
    manual: API_ENDPOINTS.REGISTRATIONS_MANUAL,
    userRoles: API_ENDPOINTS.REGISTRATIONS_USER_ROLES,
    userRolesDelete: API_ENDPOINTS.REGISTRATIONS_USER_ROLES_DELETE,
  },

  public: {
    register: API_ENDPOINTS.PUBLIC_REGISTER,
    inviteValidate: API_ENDPOINTS.PUBLIC_INVITE_VALIDATE,
    onboardingResolve: API_ENDPOINTS.PUBLIC_ONBOARDING_RESOLVE,
    onboardingStart: API_ENDPOINTS.PUBLIC_ONBOARDING_START,
    onboardingSubmissionsCreate: API_ENDPOINTS.PUBLIC_ONBOARDING_SUBMISSIONS_CREATE,
    geographyStates: API_ENDPOINTS.PUBLIC_GEOGRAPHY_STATES,
    programTypes: API_ENDPOINTS.PUBLIC_PROGRAM_TYPES,
    catalogPrograms: API_ENDPOINTS.PUBLIC_CATALOG_PROGRAMS,
    catalogProgramDetail: API_ENDPOINTS.PUBLIC_CATALOG_PROGRAM_DETAIL,
    catalogProgramDetailSlug: API_ENDPOINTS.PUBLIC_CATALOG_PROGRAM_DETAIL_SLUG,
    catalogProgramByProgram: API_ENDPOINTS.PUBLIC_CATALOG_PROGRAM_BY_PROGRAM,
    catalogProgramByProgramSlug: API_ENDPOINTS.PUBLIC_CATALOG_PROGRAM_BY_PROGRAM_SLUG,
    catalogFilters: API_ENDPOINTS.PUBLIC_CATALOG_FILTERS,
    leadsCreate: API_ENDPOINTS.PUBLIC_LEADS_CREATE,
    preRegistration: API_ENDPOINTS.PRE_REGISTRATION_PUBLIC,
    preRegistrationComplete: API_ENDPOINTS.PRE_REGISTRATION_COMPLETE,
  },

  publicAdmin: {
    programProfilesList: API_ENDPOINTS.PUBLIC_ADMIN_PROGRAM_PROFILES_LIST,
    programProfilesCreate: API_ENDPOINTS.PUBLIC_ADMIN_PROGRAM_PROFILES_CREATE,
    programProfilesDetail: API_ENDPOINTS.PUBLIC_ADMIN_PROGRAM_PROFILES_DETAIL,
    programProfilesUpdate: API_ENDPOINTS.PUBLIC_ADMIN_PROGRAM_PROFILES_UPDATE,
    programProfilesUploadImage: API_ENDPOINTS.PUBLIC_ADMIN_PROGRAM_PROFILES_UPLOAD_IMAGE,
    programProfilesByLaunch: API_ENDPOINTS.PUBLIC_ADMIN_PROGRAM_PROFILES_BY_LAUNCH,
    programProfilesExport: API_ENDPOINTS.PUBLIC_ADMIN_PROGRAM_PROFILES_EXPORT,
    programProfilesImport: API_ENDPOINTS.PUBLIC_ADMIN_PROGRAM_PROFILES_IMPORT,
    leadsList: API_ENDPOINTS.PUBLIC_ADMIN_LEADS_LIST,
    leadsDetail: API_ENDPOINTS.PUBLIC_ADMIN_LEADS_DETAIL,
    leadsUpdate: API_ENDPOINTS.PUBLIC_ADMIN_LEADS_UPDATE,
  },

  onboarding: {
    status: API_ENDPOINTS.ONBOARDING_STATUS,
    identity: API_ENDPOINTS.ONBOARDING_IDENTITY,
    teacherDetails: API_ENDPOINTS.ONBOARDING_TEACHER_DETAILS,
    teacherDetailsPartial: API_ENDPOINTS.ONBOARDING_TEACHER_DETAILS_PARTIAL,
    adminDetails: API_ENDPOINTS.ONBOARDING_ADMIN_DETAILS,
    adminDetailsPartial: API_ENDPOINTS.ONBOARDING_ADMIN_DETAILS_PARTIAL,
    corporateDetails: API_ENDPOINTS.ONBOARDING_CORPORATE_DETAILS,
    corporateDetailsPartial: API_ENDPOINTS.ONBOARDING_CORPORATE_DETAILS_PARTIAL,
    corporateOperator: API_ENDPOINTS.ONBOARDING_CORPORATE_OPERATOR,
    corporateOperatorPartial: API_ENDPOINTS.ONBOARDING_CORPORATE_OPERATOR_PARTIAL,
    studentDetails: API_ENDPOINTS.ONBOARDING_STUDENT_DETAILS,
    studentDetailsPartial: API_ENDPOINTS.ONBOARDING_STUDENT_DETAILS_PARTIAL,
    setProgramContext: API_ENDPOINTS.ONBOARDING_SET_PROGRAM_CONTEXT,
    documentsNotes: API_ENDPOINTS.ONBOARDING_DOCUMENTS_NOTES,
    submit: API_ENDPOINTS.ONBOARDING_SUBMIT,
  },

  sales: {
    scholarships: {
      list: API_ENDPOINTS.SCHOLARSHIPS_LIST,
      create: API_ENDPOINTS.SCHOLARSHIPS_CREATE,
      detail: API_ENDPOINTS.SCHOLARSHIPS_DETAIL,
      update: API_ENDPOINTS.SCHOLARSHIPS_UPDATE,
      delete: API_ENDPOINTS.SCHOLARSHIPS_DELETE,
    },
    tradeAgreements: {
      list: API_ENDPOINTS.TRADE_AGREEMENTS_LIST,
      create: API_ENDPOINTS.TRADE_AGREEMENTS_CREATE,
      detail: API_ENDPOINTS.TRADE_AGREEMENTS_DETAIL,
      update: API_ENDPOINTS.TRADE_AGREEMENTS_UPDATE,
      delete: API_ENDPOINTS.TRADE_AGREEMENTS_DELETE,
    },
    tradeDiscounts: {
      list: API_ENDPOINTS.TRADE_DISCOUNTS_LIST,
      create: API_ENDPOINTS.TRADE_DISCOUNTS_CREATE,
      detail: API_ENDPOINTS.TRADE_DISCOUNTS_DETAIL,
      update: API_ENDPOINTS.TRADE_DISCOUNTS_UPDATE,
      delete: API_ENDPOINTS.TRADE_DISCOUNTS_DELETE,
    },
    paymentSchemes: {
      list: API_ENDPOINTS.PAYMENT_SCHEMES_LIST,
      detail: API_ENDPOINTS.PAYMENT_SCHEMES_DETAIL,
    },
    pricing: {
      list: API_ENDPOINTS.PRICING_LISTS_LIST,
      detail: API_ENDPOINTS.PRICING_LISTS_DETAIL,
      create: API_ENDPOINTS.PRICING_LISTS_CREATE,
      update: API_ENDPOINTS.PRICING_LISTS_UPDATE,
      publish: API_ENDPOINTS.PRICING_LISTS_PUBLISH,
      clone: API_ENDPOINTS.PRICING_LISTS_CLONE,
    },
    tradePromotions: {
      list: API_ENDPOINTS.TRADE_PROMOTIONS_LIST,
      create: API_ENDPOINTS.TRADE_PROMOTIONS_CREATE,
      detail: API_ENDPOINTS.TRADE_PROMOTIONS_DETAIL,
      update: API_ENDPOINTS.TRADE_PROMOTIONS_UPDATE,
      delete: API_ENDPOINTS.TRADE_PROMOTIONS_DELETE,
    },
  },

  testing: {
    playwright: {
      definitions: {
        list: API_ENDPOINTS.TESTING_PLAYWRIGHT_DEFINITIONS_LIST,
        create: API_ENDPOINTS.TESTING_PLAYWRIGHT_DEFINITIONS_CREATE,
        upload: API_ENDPOINTS.TESTING_PLAYWRIGHT_DEFINITIONS_UPLOAD,
        execute: API_ENDPOINTS.TESTING_PLAYWRIGHT_DEFINITIONS_EXECUTE,
      },
      executions: API_ENDPOINTS.TESTING_PLAYWRIGHT_EXECUTIONS_LIST,
      queue: API_ENDPOINTS.TESTING_PLAYWRIGHT_QUEUE_STATS,
      job: API_ENDPOINTS.TESTING_PLAYWRIGHT_JOB_STATUS,
      codegen: {
        start: API_ENDPOINTS.TESTING_PLAYWRIGHT_CODEGEN_START,
        script: API_ENDPOINTS.TESTING_PLAYWRIGHT_CODEGEN_SCRIPT,
        upload: API_ENDPOINTS.TESTING_PLAYWRIGHT_CODEGEN_UPLOAD,
        status: API_ENDPOINTS.TESTING_PLAYWRIGHT_CODEGEN_STATUS,
        delete: API_ENDPOINTS.TESTING_PLAYWRIGHT_CODEGEN_DELETE,
      },
    },
  },
};

export default API_ENDPOINTS;
